/**
 * GC / self-perform extras — Qty + Duration from BP-700-9000-1, scaled to this job.
 */
import React, { useMemo, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { exportFilename } from '../../utils/nomenclature';
import { applyLetterhead } from '../../utils/pdfLetterhead';
import { addExtrasWorksheet, addHorizontalPdfTables, downloadBlob } from '../../utils/planExport';
import {
  extrasByCategory,
  extrasHorizontalRows,
  extrasNoteLines,
  extrasTableRows,
  GC_EXTRAS_SOURCE,
  resolveExtras,
  saveExtrasCfg,
  suggestedForItem,
  TEMPLATE_SCHEDULE_MONTHS
} from '../../utils/gcIndirectExtras';

async function exportExtrasXlsx({ jobNumber, cfg, projectMonths, months }) {
  const ExcelJS = (await import('exceljs')).default;
  const wb = new ExcelJS.Workbook();
  wb.creator = 'LGL Portal';
  addExtrasWorksheet(wb, { jobNumber, cfg, projectMonths, months });
  const buf = await wb.xlsx.writeBuffer();
  downloadBlob(
    exportFilename({ job: jobNumber, reportSlug: 'indirect-extras', ext: 'xlsx' }),
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  );
}

async function buildExtrasPdf({ jobNumber, instance, accounts, cfg, projectMonths, months }) {
  const [{ default: jsPDF }, autoTableMod] = await Promise.all([
    import('jspdf'),
    import('jspdf-autotable')
  ]);
  const autoTable = autoTableMod.default;
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'letter' });
  const { y: letterheadY } = await applyLetterhead(doc, { instance, accounts, jobNumber, margin: 14 });
  doc.setFontSize(14);
  doc.setTextColor(15, 23, 42);
  doc.text('Indirect extras — GC and self-perform', 14, letterheadY);
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text(
    `${GC_EXTRAS_SOURCE}. ${TEMPLATE_SCHEDULE_MONTHS}-month Excel takeoff scaled to ${projectMonths || '—'} months on this job.`,
    14,
    letterheadY + 5,
    { maxWidth: 180 }
  );
  autoTable(doc, {
    startY: letterheadY + 10,
    head: [['Code', 'Cost item', 'Unit', 'Qty', 'Months', 'Qty × mo']],
    body: extrasTableRows(cfg, projectMonths).map((r) => [r[0], r[2], r[3], r[4], r[5], r[6]]),
    styles: { fontSize: 7.5, cellPadding: 1.3 },
    headStyles: { fillColor: [124, 58, 237], fontSize: 8 },
    alternateRowStyles: { fillColor: [245, 243, 255] }
  });
  if (months.length) {
    doc.addPage('letter', 'landscape');
    addHorizontalPdfTables(doc, autoTable, {
      title: 'Indirects by month',
      columns: months,
      rows: extrasHorizontalRows(cfg, months, projectMonths),
      startY: 16,
      headColor: [124, 58, 237]
    });
  }
  doc.setFontSize(7.5);
  doc.setTextColor(100, 116, 139);
  let y = doc.lastAutoTable.finalY + 6;
  const pageH = doc.internal.pageSize.getHeight();
  extrasNoteLines(jobNumber, projectMonths).forEach((line) => {
    if (y > pageH - 12) { doc.addPage(); y = 14; }
    doc.text(line, 14, y, { maxWidth: doc.internal.pageSize.getWidth() - 28 });
    y += 4.5;
  });
  return doc;
}

const th = {
  textAlign: 'left',
  padding: '0.3rem 0.45rem',
  borderBottom: '2px solid #e2e8f0',
  fontSize: '0.66rem',
  color: '#64748b',
  letterSpacing: '0.03em',
  textTransform: 'uppercase'
};

export default function IndirectExtrasModal({
  open,
  onClose,
  jobNumber,
  cfg,
  onCfg,
  projectMonths = 0,
  months = []
}) {
  const { instance, accounts } = useMsal();
  const [exporting, setExporting] = useState(null);
  const [showEmpty, setShowEmpty] = useState(false);
  const resolved = useMemo(() => resolveExtras(cfg, projectMonths), [cfg, projectMonths]);
  const visible = useMemo(
    () => (showEmpty ? resolved : resolved.filter((item) => item.include || item.suggested.include)),
    [resolved, showEmpty]
  );
  const groups = useMemo(() => extrasByCategory(visible), [visible]);
  const includedCount = resolved.filter((item) => item.include).length;

  if (!open) return null;

  const patch = (next) => {
    onCfg?.(next);
    saveExtrasCfg(jobNumber, next);
  };
  const toggle = (id) => {
    const item = resolved.find((r) => r.id === id);
    const on = item?.include;
    patch({
      ...cfg,
      included: { ...(cfg?.included || {}), [id]: !on }
    });
  };
  const setField = (id, field, value) => patch({
    ...cfg,
    [field]: { ...(cfg?.[field] || {}), [id]: value }
  });
  const resetSuggested = () => patch({ included: {}, qty: {}, duration: {} });

  const onExportXlsx = async () => {
    setExporting('xlsx');
    try {
      await exportExtrasXlsx({ jobNumber, cfg, projectMonths, months });
    } catch (err) {
      console.error('Extras xlsx export failed:', err);
      alert(`xlsx export failed: ${err.message || err}`);
    } finally {
      setExporting(null);
    }
  };
  const onExportPdf = async () => {
    setExporting('pdf');
    try {
      const doc = await buildExtrasPdf({ jobNumber, instance, accounts, cfg, projectMonths, months });
      doc.save(exportFilename({ job: jobNumber, reportSlug: 'indirect-extras', ext: 'pdf' }));
    } catch (err) {
      console.error('Extras PDF export failed:', err);
      alert(`PDF export failed: ${err.message || err}`);
    } finally {
      setExporting(null);
    }
  };

  const exportBtn = (label, onClick, { solid = false } = {}) => (
    <button
      type="button"
      onClick={onClick}
      disabled={!!exporting}
      style={{
        border: '1px solid #0f766e', background: solid ? '#0f766e' : 'white',
        color: solid ? 'white' : '#0f766e',
        borderRadius: 7, fontSize: '0.75rem', fontWeight: 800, padding: '0.35rem 0.7rem',
        cursor: exporting ? 'wait' : 'pointer', whiteSpace: 'nowrap'
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Indirect extras"
        style={{
          background: 'white', borderRadius: 12, width: 'min(980px, 96vw)', maxHeight: '88vh',
          overflow: 'auto', boxShadow: '0 24px 60px rgba(15, 23, 42, 0.35)'
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '0.85rem 1rem',
          borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, background: 'white', zIndex: 1
        }}>
          <div>
            <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '1rem' }}>Indirect extras</div>
            <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>
              Excel takeoff is {TEMPLATE_SCHEDULE_MONTHS} months. This job is {projectMonths || '—'} months —
              Duration is scaled, Qty is the sheet headcount. {includedCount} items in the estimate (empty Excel rows hidden).
              Override any number; that is the base for the indirects estimate.
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            {exportBtn(exporting === 'xlsx' ? 'Exporting…' : '⬇ xlsx', onExportXlsx, { solid: true })}
            {exportBtn(exporting === 'pdf' ? 'Exporting…' : '⬇ PDF', onExportPdf)}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{ border: 'none', background: 'transparent', fontSize: 20, color: '#64748b', cursor: 'pointer', lineHeight: 1 }}
          >
            ×
          </button>
        </div>
        <div style={{ padding: '0.65rem 1rem', display: 'flex', gap: 8, borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
          <button type="button" onClick={resetSuggested} style={{
            border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: 6,
            fontSize: '0.72rem', padding: '0.25rem 0.55rem', cursor: 'pointer'
          }}>Reset to suggested</button>
          <button type="button" onClick={() => setShowEmpty((v) => !v)} style={{
            border: '1px solid #cbd5e1', background: showEmpty ? '#ede9fe' : '#f8fafc', borderRadius: 6,
            fontSize: '0.72rem', padding: '0.25rem 0.55rem', cursor: 'pointer'
          }}>{showEmpty ? 'Hide empty Excel rows' : 'Show empty Excel rows'}</button>
        </div>
        <div style={{ padding: '0.6rem 1rem 1rem' }}>
          {groups.map((g) => (
            <div key={g.category} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 800, color: '#5b21b6', margin: '8px 0 4px' }}>
                {g.category}
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: 36 }}>In</th>
                    <th style={th}>Cost item</th>
                    <th style={{ ...th, width: 90 }}>Unit</th>
                    <th style={{ ...th, width: 70 }}>Qty</th>
                    <th style={{ ...th, width: 88 }}>Months</th>
                    <th style={{ ...th, width: 72 }}>Qty × mo</th>
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((item, i) => {
                    const sug = suggestedForItem(item, projectMonths);
                    const on = item.include;
                    const total = Math.round(((Number(item.qty) || 0) * (Number(item.duration) || 0) + Number.EPSILON) * 100) / 100;
                    return (
                      <tr key={item.id} style={{ background: i % 2 === 0 ? '#faf5ff' : 'white', opacity: on ? 1 : 0.45 }}>
                        <td style={{ padding: '0.28rem 0.45rem' }}>
                          <input
                            type="checkbox"
                            checked={on}
                            onChange={() => toggle(item.id)}
                            style={{ accentColor: '#7c3aed' }}
                          />
                        </td>
                        <td style={{ padding: '0.28rem 0.45rem', color: '#334155' }}>
                          <span style={{ color: '#64748b', fontVariantNumeric: 'tabular-nums', marginRight: 8 }}>{item.id}</span>
                          {item.name}
                          {item.comment ? (
                            <div style={{ fontSize: '0.64rem', color: '#94a3b8' }}>{item.comment}</div>
                          ) : null}
                        </td>
                        <td style={{ padding: '0.28rem 0.45rem', color: '#64748b', whiteSpace: 'nowrap' }}>{item.unit}</td>
                        <td style={{ padding: '0.28rem 0.45rem' }}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={item.qty}
                            onChange={(e) => setField(item.id, 'qty', e.target.value)}
                            disabled={!on}
                            title={`Excel suggested ${sug.qty}`}
                            style={{
                              width: 58, fontSize: '0.75rem', border: '1px solid #cbd5e1',
                              borderRadius: 5, padding: '2px 5px'
                            }}
                          />
                        </td>
                        <td style={{ padding: '0.28rem 0.45rem' }}>
                          <input
                            type="text"
                            inputMode="decimal"
                            value={item.duration}
                            onChange={(e) => setField(item.id, 'duration', e.target.value)}
                            disabled={!on}
                            title={`Excel suggested ${sug.duration} months at this job length`}
                            style={{
                              width: 64, fontSize: '0.75rem', border: '1px solid #cbd5e1',
                              borderRadius: 5, padding: '2px 5px'
                            }}
                          />
                        </td>
                        <td style={{ padding: '0.28rem 0.45rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#475569' }}>
                          {total || ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
